import { useCallback, useEffect, useRef, useState } from "react";
import { errorText } from "../../i18n/serverErrors";
import { createPortal } from "react-dom";
import {
  shopModeApi,
  type FloorPhotoSearchCandidate,
  type FloorPhotoSearchResult,
} from "../../api/shopMode";
import {
  VIDEO_CONSTRAINTS,
  cameraErrorMessage,
  cameraSupported,
  stopStream,
  tryApplyContinuousFocus,
} from "../../utils/cameraStream";
import { captureStillFromVideo } from "../../utils/captureVideoFrame";
import { PhotoSearchCandidateCard } from "./PhotoSearchCandidateCard";

type Step = "camera" | "searching" | "result" | "error";

export interface ShopFloorPhotoSearchProps {
  /** The florist picked an item; the server has already logged the look-up. */
  onPicked(item: FloorPhotoSearchCandidate): void;
  /** Nothing fitted, or the florist gave up. The finder shows a note. */
  onDismissed(): void;
  /** Backed out before searching. */
  onCancel(): void;
}

/**
 * G-80: aim the camera at a flower, tap Find, pick what it is.
 *
 * Own camera view rather than the system camera app: the florist stays inside
 * the kiosk, the still is ~300 KB instead of a 5–10 MB phone photo, and there
 * is no confirm screen between "aim" and "find". The camera request itself is
 * the barcode scanner's, shared via `utils/cameraStream`.
 *
 * Portalled to the body for the same reason the scan resolver is: the stock
 * panel carries a `backdrop-filter`, which would trap this full-screen view
 * inside a 200-pixel-wide box.
 *
 * The pick is written by the server (ITEM_LOOKED_UP, how: PHOTO); this
 * component never calls `floorRecordLookup`, or one look-up would log twice —
 * the exact doubling G-75 had to fix.
 */
import { useT } from "../../i18n/LanguageProvider";
import { t as translate } from "../../i18n/t";
import { colourLabel, flowerTypeLabel } from "../../i18n/taxonomy";
export function ShopFloorPhotoSearch({
  onPicked,
  onDismissed,
  onCancel,
}: ShopFloorPhotoSearchProps) {
  const { t } = useT();
  const [step, setStep] = useState<Step>("camera");
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [result, setResult] = useState<FloorPhotoSearchResult | null>(null);
  const [stillUrl, setStillUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // The camera runs only while the camera step is showing.
  useEffect(() => {
    if (step !== "camera") return;
    let cancelled = false;
    const video = videoRef.current;
    setCameraError(null);
    setCameraReady(false);
    const start = async () => {
      if (!cameraSupported()) {
        setCameraError(translate("photoSearch.noCameraSupport"));
        return;
      }
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: VIDEO_CONSTRAINTS,
          audio: false,
        });
      } catch (err) {
        if (cancelled) return;
        setCameraError(cameraErrorMessage(err));
        return;
      }
      if (cancelled || !video) {
        stopStream(stream);
        return;
      }
      streamRef.current = stream;
      void tryApplyContinuousFocus(stream);
      video.srcObject = stream;
      video.setAttribute("playsinline", "true");
      try {
        await video.play();
      } catch {
        /* autoplay quirk — frames still arrive */
      }
    };
    void start();
    return () => {
      cancelled = true;
      stopStream(streamRef.current);
      streamRef.current = null;
      if (video?.srcObject instanceof MediaStream) {
        stopStream(video.srcObject);
        video.srcObject = null;
      }
    };
  }, [step]);

  // The captured still is an object URL; release it when it is replaced or the screen closes.
  useEffect(() => {
    return () => {
      if (stillUrl) URL.revokeObjectURL(stillUrl);
    };
  }, [stillUrl]);

  const find = useCallback(async () => {
    const video = videoRef.current;
    if (!video || busy || !cameraReady) return;
    setBusy(true);
    let still: Blob;
    try {
      still = await captureStillFromVideo(video);
    } catch {
      // No frame yet (iOS may refuse play() until a gesture): stay on the
      // camera, say so, and let the florist tap again — not the AI's fault.
      setCameraError(translate("photoSearch.noPictureYet"));
      setBusy(false);
      return;
    }
    try {
      setStillUrl(URL.createObjectURL(still));
      setStep("searching");
      const answer = await shopModeApi.floorPhotoSearch(still);
      setResult(answer);
      setStep("result");
    } catch (err) {
      // A 503 carries the server's own plain sentence (switched off, budget
      // used up, not answering); anything else is a plain sentence too.
      setErrorMsg(
        errorText(err, "photoSearch.searchFailed"),
      );
      setStep("error");
    } finally {
      setBusy(false);
    }
  }, [busy, cameraReady]);

  /** Tell the server what the florist decided; a lost answer never blocks the florist. */
  const answer = useCallback(
    async (itemId: string | null) => {
      if (!result?.searchId) return;
      try {
        await shopModeApi.floorPhotoSearchPick(result.searchId, itemId);
      } catch (err) {
        // The label is valuable, the customer at the counter more so.
        console.warn("photo search pick was not recorded", err);
      }
    },
    [result],
  );

  /** One guarded path for every way the florist answers: record, then do the one thing. */
  const respond = useCallback(
    async (itemId: string | null, then: () => void) => {
      if (busy) return;
      setBusy(true);
      try {
        await answer(itemId);
      } finally {
        setBusy(false);
      }
      then();
    },
    [answer, busy],
  );
  const pick = (candidate: FloorPhotoSearchCandidate) =>
    respond(candidate.id, () => onPicked(candidate));
  const noneOfThese = () => respond(null, onDismissed);
  const tryAgain = () =>
    respond(null, () => {
      setResult(null);
      setErrorMsg(null);
      setCameraError(null);
      setStep("camera");
    });

  // What the model saw, as chips in the florist's language: the type (the
  // model's English word; a shop type of that name is translated), then the
  // colour (the model is told to use the shop's colour names).
  const looksLike = result?.isFlower
    ? [
        result.flowerType ? flowerTypeLabel(result.flowerType.trim().toUpperCase(), result.flowerType) : null,
        result.colour ? colourLabel(result.colour) : null,
      ].filter((v): v is string => Boolean(v))
    : [];

  if (step === "camera") {
    return createPortal(
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("photoSearch.title")}
        className="sf-ps sf-ps--stage"
      >
        <header className="sf-ps-bar">
          <button type="button" className="sf-ps-close" onClick={onCancel}>
            <CloseIcon />
            <span>{t("common.close")}</span>
          </button>
          <h2 className="sf-ps-bar-title">{t("photoSearch.title")}</h2>
        </header>
        <div className="sf-ps-viewfinder">
          <video
            ref={videoRef}
            muted
            playsInline
            autoPlay
            className="sf-ps-video"
            onLoadedData={(event) => {
              const video = event.currentTarget;
              setCameraReady(video.videoWidth > 0 && video.videoHeight > 0);
            }}
          />
          <span className="sf-ps-frame" aria-hidden="true">
            <span />
            <span />
            <span />
            <span />
          </span>
          {cameraError ? (
            <div className="alert error sf-ps-viewfinder-note">{cameraError}</div>
          ) : (
            <p className="sf-ps-hint">{t("photoSearch.fillFrame")}</p>
          )}
        </div>
        <div className="sf-ps-shutter-row">
          <button
            type="button"
            className="sf-ps-shutter"
            onClick={() => void find()}
            disabled={busy || !cameraReady}
          >
            <CameraIcon />
            <span>{t("photoSearch.find")}</span>
          </button>
        </div>
      </div>,
      document.body,
    );
  }

  if (step === "searching") {
    return createPortal(
      <div className="sf-ps sf-ps--stage" aria-live="polite">
        <div className="sf-ps-viewfinder sf-ps-viewfinder--still">
          {stillUrl ? <img src={stillUrl} alt="" className="sf-ps-video" /> : null}
          <div className="sf-ps-looking">
            <span className="sf-ps-ring" aria-hidden="true" />
            <p className="sf-ps-looking-title">{t("photoSearch.looking")}</p>
            <p className="sf-ps-looking-sub">{t("photoSearch.takesAFewSeconds")}</p>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  if (step === "result" && result) {
    const nothingInStock = result.stockListSize === 0;
    const notFlower = result.isFlower === false;
    const noMatch = !nothingInStock && !notFlower && result.candidates.length === 0;
    const title = nothingInStock
      ? t("photoSearch.noFlowersInStock")
      : notFlower
        ? t("photoSearch.notAFlower")
        : noMatch
          ? t("photoSearch.nothingLooksLikeIt")
          : t("photoSearch.whichOne");
    const [best, ...others] = result.candidates;
    const candidateButton = (candidate: FloorPhotoSearchCandidate, isBest: boolean) => (
      <PhotoSearchCandidateCard
        key={candidate.id}
        candidate={candidate}
        best={isBest}
        onPick={() => void pick(candidate)}
        disabled={busy}
      />
    );
    return createPortal(
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="sf-ps-heading"
        className="sf-scan-overlay sf-ps sf-ps--sheet"
      >
        <div className="sf-ps-result">
          <aside className="sf-ps-photo-col">
            {stillUrl ? <img src={stillUrl} alt="" className="sf-ps-photo" /> : null}
            {looksLike.length > 0 ? (
              <div className="sf-ps-looks">
                <span className="sf-ps-looks-label">{t("photoSearch.looksLike")}</span>
                {looksLike.map((word, index) => (
                  <span key={index} className="sf-ps-chip">
                    {word}
                  </span>
                ))}
              </div>
            ) : null}
          </aside>
          <section className="sf-ps-list-col">
            <h2 id="sf-ps-heading" className="sf-ps-heading">{title}</h2>
            {notFlower && result.notes ? <p className="sf-ps-note">{result.notes}</p> : null}
            {best ? (
              <div className="sf-ps-group">
                <span className="sf-ps-eyebrow">{t("photoSearch.bestMatch")}</span>
                {candidateButton(best, true)}
              </div>
            ) : null}
            {others.length > 0 ? (
              <div className="sf-ps-group">
                <span className="sf-ps-eyebrow">{t("photoSearch.alsoPossible")}</span>
                {others.map((c) => candidateButton(c, false))}
              </div>
            ) : null}
            <div className="sf-ps-actions">
              {result.candidates.length > 0 ? (
                <button
                  type="button"
                  className="button tinted"
                  onClick={() => void noneOfThese()}
                  disabled={busy}
                >
                  {t("photoSearch.noneOfThese")}
                </button>
              ) : null}
              {!nothingInStock ? (
                <button
                  type="button"
                  className="button plain"
                  onClick={() => void tryAgain()}
                  disabled={busy}
                >
                  <CameraIcon />
                  {t("photoSearch.tryAnotherPhoto")}
                </button>
              ) : null}
              {result.candidates.length === 0 ? (
                <button
                  type="button"
                  className="button plain"
                  onClick={() => void noneOfThese()}
                  disabled={busy}
                >
                  {t("common.done")}
                </button>
              ) : null}
            </div>
          </section>
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="sf-ps-heading"
      className="sf-scan-overlay sf-ps sf-ps--sheet"
    >
      <div className="sf-ps-result sf-ps-result--single">
        <section className="sf-ps-list-col">
          <h2 id="sf-ps-heading" className="sf-ps-heading">{t("photoSearch.didNotWork")}</h2>
          {errorMsg ? <div className="alert error">{errorMsg}</div> : null}
          <div className="sf-ps-actions">
            <button
              type="button"
              className="button tinted"
              onClick={() => {
                setErrorMsg(null);
                setStep("camera");
              }}
            >
              <CameraIcon />
              {t("photoSearch.tryAgain")}
            </button>
            <button type="button" className="button plain" onClick={onCancel}>
              {t("common.done")}
            </button>
          </div>
        </section>
      </div>
    </div>,
    document.body,
  );
}

function CameraIcon() {
  return (
    <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 8h3l2-3h6l2 3h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
      <circle cx="12" cy="13.5" r="3.5" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}
